import { supabase } from './supabase';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string) || '';

export class AuthRequiredError extends Error {
  constructor() {
    super('auth required: refresh failed');
    this.name = 'AuthRequiredError';
  }
}

async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * fetch() wrapper that:
 *   1. injects the current Supabase access token as Bearer
 *   2. on 401, transparently refreshes the session and retries once
 *   3. throws AuthRequiredError only if refresh itself fails
 *      (UI listens for the 'eva-field:auth-required' event in that case)
 */
export async function fetchWithAuth(
  path: string,
  init: RequestInit = {},
  opts: { orgId?: string | null } = {},
): Promise<Response> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  const send = async (token: string | null): Promise<Response> => {
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const activeOrgId =
      opts.orgId ??
      (typeof localStorage !== 'undefined' ? localStorage.getItem('activeOrgId') : null);
    if (activeOrgId) headers.set('X-Org-Id', activeOrgId);
    return fetch(url, { ...init, headers });
  };

  const token = await getAccessToken();
  const resp = await send(token);
  if (resp.status !== 401) return resp;

  const { data, error } = await supabase.auth.refreshSession();
  if (error || !data.session) throw new AuthRequiredError();
  return send(data.session.access_token);
}
